import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "react-toastify";
import Pagination from "../Pagination";
import CustomSelect from "../CustomSelectField/CustomSelect";
import useReviewGarage from "../../hook/Usereviewgarage";
import ReviewActionDialog from "./Reviewactiondialog";

// API status "visible" is shown as "Published" in the UI.
const getReviewStatusLabel = (status) =>
  status === "visible" ? "Published" : status || "-";

const getReviewStatusClass = (status) => {
  switch (status) {
    case "visible":
      return "bg-success-focus text-success-600 border-success-main";
    case "removed":
      return "bg-danger-focus text-danger-600 border-danger-main";
    case "flagged":
      return "bg-warning-focus text-warning-600 border-warning-main";
    default:
      return "bg-secondary-focus text-secondary-light border";
  }
};

const Stars = ({ value = 0 }) => {
  const rating = Math.max(0, Math.min(5, Number(value) || 0));

  return (
    <span className="text-nowrap" title={`${rating} / 5`}>
      <span className="text-warning">{"★".repeat(Math.floor(rating))}</span>
      <span className="text-secondary-light">
        {"★".repeat(5 - Math.floor(rating))}
      </span>
      <span className="text-secondary-light ms-1">({rating}/5)</span>
    </span>
  );
};

const AllGarageReviewsLayer = () => {
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [garageFilter, setGarageFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [ratingFilter, setRatingFilter] = useState("");

  // { type: "approve" | "remove" | "restore", reviewId, reasons }
  const [dialog, setDialog] = useState(null);

  const {
    garages,
    users,
    records,
    loading,
    counts,
    pagination,
    refresh,
    actionLoadingId,
    approveReview,
    removeReview,
    restoreReview,
    fetchRemovalReasons,
  } = useReviewGarage({
    page,
    limit,
    query,
    status: statusFilter,
    garageId: garageFilter,
    userId: userFilter,
    rating: ratingFilter,
    onError: (message) => toast.error(message),
  });

  const { total, totalPages, hasNext, hasPrev } = pagination;

  useEffect(() => {
    const timeout = setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
    }, 500);

    return () => clearTimeout(timeout);
  }, [search]);

  const statusTabs = [
    { value: "", label: "All", count: counts.total },
    { value: "visible", label: "Published", count: counts.visible },
    { value: "flagged", label: "Flagged", count: counts.flagged },
    { value: "removed", label: "Removed", count: counts.removed },
  ];

  const handleView = (id) => {
    navigate("/garage-reviews-detail/" + id);
  };

  const closeDialog = () => {
    if (actionLoadingId) return;
    setDialog(null);
  };

  const openApprove = (reviewId) =>
    setDialog({ type: "approve", reviewId, reasons: [] });

  const openRestore = (reviewId) =>
    setDialog({ type: "restore", reviewId, reasons: [] });

  const openRemove = async (reviewId) => {
    try {
      const reasons = await fetchRemovalReasons(reviewId);

      if (reasons.length === 0) {
        toast.error("No removal reasons were returned by the API.");
        return;
      }

      setDialog({ type: "remove", reviewId, reasons });
    } catch (error) {
      console.error("Error fetching removal reasons:", error);
      toast.error(error.message || "Failed to fetch removal reasons.");
    }
  };

  const handleDialogConfirm = async ({ reason, note }) => {
    if (!dialog) return;

    const { type, reviewId } = dialog;

    try {
      if (type === "approve") {
        await approveReview(reviewId, note);
        toast.success("Review approved.");
      } else if (type === "remove") {
        await removeReview(reviewId, reason, note);
        toast.success("Review removed.");
      } else if (type === "restore") {
        await restoreReview(reviewId, note);
        toast.success("Review restored.");
      }

      setDialog(null);
      refresh();
    } catch (error) {
      console.error(`Error during ${type} review:`, error);
      toast.error(error.message || `Failed to ${type} review.`);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-body p-24 pb-0">
        <div className="row gy-4">
          <div className="col-xxl-3 col-sm-6">
            <div className="card p-3 radius-12 shadow-none border h-100">
              <div className="d-flex align-items-center gap-3">
                <div className="w-50-px h-50-px radius-12 bg-primary-50 d-flex justify-content-center align-items-center">
                  <Icon
                    icon="solar:chat-round-line-bold"
                    className="text-primary-600"
                    width="26"
                  />
                </div>
                <div>
                  <span className="text-secondary-light text-sm fw-medium">
                    Total Reviews
                  </span>
                  <h6 className="mb-0 mt-1">{counts.total}</h6>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xxl-3 col-sm-6">
            <div className="card p-3 radius-12 shadow-none border h-100">
              <div className="d-flex align-items-center gap-3">
                <div className="w-50-px h-50-px radius-12 bg-success-focus d-flex justify-content-center align-items-center">
                  <Icon
                    icon="solar:check-circle-bold"
                    className="text-success-600"
                    width="26"
                  />
                </div>
                <div>
                  <span className="text-secondary-light text-sm fw-medium">
                    Published Reviews
                  </span>
                  <h6 className="mb-0 mt-1">{counts.visible}</h6>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xxl-3 col-sm-6">
            <div className="card p-3 radius-12 shadow-none border h-100">
              <div className="d-flex align-items-center gap-3">
                <div className="w-50-px h-50-px radius-12 bg-warning-focus d-flex justify-content-center align-items-center">
                  <Icon
                    icon="solar:flag-bold"
                    className="text-warning-600"
                    width="26"
                  />
                </div>
                <div>
                  <span className="text-secondary-light text-sm fw-medium">
                    Flagged Reviews
                  </span>
                  <h6 className="mb-0 mt-1">{counts.flagged}</h6>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xxl-3 col-sm-6">
            <div className="card p-3 radius-12 shadow-none border h-100">
              <div className="d-flex align-items-center gap-3">
                <div className="w-50-px h-50-px radius-12 bg-danger-focus d-flex justify-content-center align-items-center">
                  <Icon
                    icon="solar:eye-closed-bold"
                    className="text-danger-600"
                    width="26"
                  />
                </div>
                <div>
                  <span className="text-secondary-light text-sm fw-medium">
                    Removed Reviews
                  </span>
                  <h6 className="mb-0 mt-1">{counts.removed}</h6>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        {/* Status tabs */}
        <ul className="nav nav-pills gap-2 w-100" role="tablist">
          {statusTabs.map((tab) => {
            const isActive = statusFilter === tab.value;

            return (
              <li className="nav-item" role="presentation" key={tab.label}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`nav-link btn btn-sm px-16 py-8 radius-8 fw-medium ${
                    isActive ? "active" : "text-secondary-light"
                  }`}
                  style={
                    isActive
                      ? {
                          backgroundColor: "#0039BF",
                          color: "#fff",
                          borderColor: "#0039BF",
                        }
                      : {}
                  }
                  onClick={() => {
                    setStatusFilter(tab.value);
                    setPage(0);
                  }}
                >
                  {tab.label}
                  <span className="ms-2 text-sm">({tab.count})</span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="d-flex flex-wrap align-items-center gap-3">
          <div className="icon-field">
            <input
              type="text"
              className="form-control form-control-sm w-auto"
              placeholder="Search garage or comment"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="icon">
              <Icon icon="ion:search-outline" />
            </span>
          </div>

          <CustomSelect
            value={garageFilter}
            onChange={(e) => {
              setGarageFilter(e.target.value);
              setPage(0);
            }}
            options={[
              { value: "", label: "All garages" },
              ...garages.map((garage) => ({
                value: String(garage.id),
                label: garage.garage_name,
              })),
            ]}
          />

          <CustomSelect
            value={userFilter}
            onChange={(e) => {
              setUserFilter(e.target.value);
              setPage(0);
            }}
            options={[
              { value: "", label: "All reviewers" },
              ...users.map((user) => ({
                value: String(user.id),
                label: user.fullName || user.username || `User ${user.id}`,
              })),
            ]}
          />

          <CustomSelect
            value={ratingFilter}
            onChange={(e) => {
              setRatingFilter(e.target.value);
              setPage(0);
            }}
            options={[
              { value: "", label: "All ratings" },
              { value: "5", label: "5 Stars" },
              { value: "4", label: "4 Stars" },
              { value: "3", label: "3 Stars" },
              { value: "2", label: "2 Stars" },
              { value: "1", label: "1 Star" },
            ]}
          />
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                {/* <th scope="col">ID</th> */}
                <th scope="col">Garage</th>
                <th scope="col">Reviewer</th>
                <th scope="col">Rating</th>
                <th scope="col">Comment</th>
                <th scope="col">Date</th>
                <th scope="col">Recommendation Status</th>
                <th scope="col" className="text-center">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    No reviews found
                  </td>
                </tr>
              ) : (
                records.map((review) => {
                  const status = review.moderation?.status;
                  const comment =
                    review.review_text || review.app_review_text || "-";
                  const reviewer = review.reviewer;
                  const isActionLoading = actionLoadingId === review.id;

                  return (
                    <tr
                      key={review.id}
                      onClick={() => handleView(review.id)}
                      className="cursor-pointer"
                    >
                      {/* <td>{review.id}</td> */}

                      <td>
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {review.garage?.garage_name || "-"}
                        </span>
                      </td>

                      <td>
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {reviewer?.fullName || reviewer?.username || "-"}
                        </span>
                      </td>

                      <td>
                        <Stars value={review.rating} />
                      </td>

                      <td style={{ maxWidth: 260 }}>
                        <span
                          className="d-inline-block text-truncate w-100 text-secondary-light"
                          title={comment}
                        >
                          {comment}
                        </span>
                      </td>

                      <td>
                        {review.created_at
                          ? format(new Date(review.created_at), "dd MMM, yyyy")
                          : "-"}
                      </td>

                      <td>
                        <span
                          className={`border px-16 py-4 radius-4 fw-medium text-sm text-capitalize ${getReviewStatusClass(
                            status,
                          )}`}
                        >
                          {getReviewStatusLabel(status)}
                        </span>
                      </td>

                      <td className="text-center">
                        <div className="d-flex align-items-center gap-10 justify-content-center flex-wrap">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleView(review.id);
                            }}
                            className="bg-info-focus bg-hover-primary-200 text-primary-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            title="View Details"
                          >
                            <Icon icon="lucide:eye" className="menu-icon" />
                          </button>

                          {/* Approve is shown only for flagged reviews */}
                          {status === "flagged" && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openApprove(review.id);
                              }}
                              disabled={isActionLoading}
                              className="bg-success-focus text-success-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              title="Approve Review"
                            >
                              <Icon
                                icon="solar:check-circle-bold"
                                className="menu-icon"
                              />
                            </button>
                          )}

                          {status !== "removed" && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openRemove(review.id);
                              }}
                              disabled={isActionLoading}
                              className="bg-danger-focus text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              title="Remove Review"
                            >
                              <Icon
                                icon="fluent:delete-24-regular"
                                className="menu-icon"
                              />
                            </button>
                          )}

                          {status === "removed" && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openRestore(review.id);
                              }}
                              disabled={isActionLoading}
                              className="bg-success-focus text-success-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              title="Restore Review"
                            >
                              <Icon
                                icon="solar:restart-bold"
                                className="menu-icon"
                              />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <div className="d-flex align-items-center gap-2">
            <span>
              Showing {records.length} of {total} entries
            </span>

            <CustomSelect
              value={String(limit)}
              onChange={(e) => {
                setLimit(parseInt(e.target.value, 10));
                setPage(0);
              }}
              options={[
                { value: "10", label: "10" },
                { value: "15", label: "15" },
                { value: "20", label: "20" },
                { value: "50", label: "50" },
                { value: "100", label: "100" },
              ]}
            />
          </div>

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onPageChange={setPage}
          />
        </div>
      </div>

      <ReviewActionDialog
        isOpen={!!dialog}
        type={dialog?.type}
        reasons={dialog?.reasons || []}
        loading={!!dialog && actionLoadingId === dialog.reviewId}
        onClose={closeDialog}
        onConfirm={handleDialogConfirm}
      />
    </div>
  );
};

export default AllGarageReviewsLayer;
